import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, Chip, CircularProgress, Container, Paper, Stack, TextField, Typography } from "@mui/material";
import ClientCandidatePresentation from "../components/ClientCandidatePresentation";
import { portalRequest } from "../utils/clientPortal";
import { readVerifierToken, clearVerifierFromUrl } from "../utils/verifierToken";

export default function ClientPortal() {
  const location = useLocation(), navigate = useNavigate(), { id } = useParams();
  const accepting = location.pathname === "/client-portal/accept";
  const login = location.pathname === "/client-portal/login";
  const [inviteToken, setInviteToken] = useState(() => readVerifierToken());
  useEffect(() => {
    if (accepting) {
      const incoming = readVerifierToken();
      if (incoming) setInviteToken(incoming);
      clearVerifierFromUrl();
    } else setInviteToken("");
  }, [accepting, location.key]);
  const [user, setUser] = useState(null), [data, setData] = useState(null), [rows, setRows] = useState([]);
  const [email, setEmail] = useState(""), [password, setPassword] = useState("");
  const [error, setError] = useState(""), [busy, setBusy] = useState(false), [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    setError(""); setData(null); setLoading(true);
    if (accepting || login) { setLoading(false); return; }
    const refresh = async () => {
      try {
        const me = await portalRequest("/me");
        const payload = await portalRequest(id ? `/presentations/${id}` : "/presentations");
        if (active) { setUser(me); if (id) setData(payload); else setRows(payload.presentations); }
      } catch (e) {
        if (!active) return;
        if (e.status === 401) navigate(`/client-portal/login?next=${encodeURIComponent(location.pathname)}`, { replace: true });
        else { setError(e.message); setData(null); setRows([]); }
      } finally { if (active) setLoading(false); }
    };
    refresh();
    const timer = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(timer); };
  }, [accepting, login, id, location.pathname, navigate]);
  const submit = async (event) => {
    event.preventDefault(); setError(""); setBusy(true);
    try {
      await portalRequest(accepting ? "/accept" : "/login", { method: "POST", body: JSON.stringify(accepting
        ? { token: inviteToken, password } : { email, password }) });
      setPassword("");
      const next = new URLSearchParams(location.search).get("next");
      navigate(next && /^\/client-portal(?:\/presentations\/\d+)?$/.test(next) ? next : "/client-portal", { replace: true });
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const logout = async () => {
    try { await portalRequest("/logout", { method: "POST" }); setUser(null); setData(null); setRows([]); navigate("/client-portal/login"); }
    catch (e) { setError(e.message); }
  };
  return <Box sx={{ minHeight: "100vh", bgcolor: "#F8FAFC", pb: 6 }}>
    <Box sx={{ bgcolor: "white", borderBottom: "1px solid #EAECF0", py: 2.5 }}>
      <Container maxWidth="lg"><Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography component={RouterLink} to="/client-portal" sx={{ fontWeight: 800, fontSize: 21, color: "#6941C6", textDecoration: "none" }}>RPO <Box component="span" sx={{ color: "#344054", fontWeight: 500 }}>Client Portal</Box></Typography>
        {user && !login && !accepting && <Stack direction="row" spacing={2} alignItems="center"><Typography variant="body2" sx={{ display: { xs: "none", sm: "block" } }}>{user.email}</Typography><Button onClick={logout}>Sign out</Button></Stack>}
      </Stack></Container>
    </Box>
    <Container maxWidth={login || accepting ? "sm" : "lg"} sx={{ pt: { xs: 3, md: 5 } }}>
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {loading ? <CircularProgress aria-label="Loading Client Portal" /> : login || accepting ?
        <Paper component="form" method="post" onSubmit={submit} variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{accepting ? "Accept your invitation" : "Welcome to your Client Portal"}</Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>{accepting ? "Create a password with at least 12 characters. If you already have a Client Portal account, use its existing password." : "Sign in to review candidate presentations for your team."}</Typography>
          <Stack spacing={3}>
            {!accepting && <TextField label="Email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}
            <TextField label="Password" type="password" autoComplete={accepting ? "new-password" : "current-password"} value={password} onChange={(e) => setPassword(e.target.value)} required inputProps={{ minLength: accepting ? 12 : undefined }} />
            <Button variant="contained" type="submit" disabled={busy}>{busy ? "Please wait…" : accepting ? "Accept invitation" : "Sign in"}</Button>
            <Typography variant="body2" color="text.secondary">Need access or help signing in? Contact your RPO recruiter.</Typography>
          </Stack>
        </Paper> : id ? <>
          <Button component={RouterLink} to="/client-portal" sx={{ mb: 2 }}>← All presentations</Button>
          {data && <ClientCandidatePresentation data={data} />}
        </> : <>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Candidate presentations</Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>Meet the candidates your RPO team recommends for your jobs.</Typography>
          {!rows.length && !error && <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}><Typography>No candidates have been presented for your assigned jobs yet.</Typography></Paper>}
          <Stack spacing={2}>{rows.map((row) => <Paper key={row.id} variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={2} alignItems={{ sm: "center" }}>
              <Box><Stack direction="row" spacing={1} alignItems="center"><Typography sx={{ fontWeight: 600 }}>{row.notification_title || `New candidate presented for ${row.job_title}`}</Typography>{!row.read_at && <Chip size="small" color="primary" label="New" />}</Stack>
                <Typography variant="h6" sx={{ mt: 1 }}>{row.candidate_name}</Typography><Typography color="text.secondary">{row.company_name} · {row.job_title}</Typography></Box>
              <Button component={RouterLink} to={`/client-portal/presentations/${row.id}`} variant="outlined">View candidate</Button>
            </Stack>
          </Paper>)}</Stack>
        </>}
    </Container>
  </Box>;
}
